import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { ConfirmProvider } from './components/ConfirmDialog';
import { ToastProvider } from './components/ToastProvider';
import { initAnalytics, trackPageview, trackOnce } from './lib/analytics';
import { getConsent, setConsent } from './lib/consent';
import './i18n';
import './index.css';

// After a redeploy the HTML may reference old chunk hashes; a lazy import/CSS preload then
// 404s and React renders nothing (blank page). Recover by reloading once to fetch the
// fresh assets. The flag prevents a reload loop if the chunk is genuinely gone.
window.addEventListener('vite:preloadError', () => {
  if (!sessionStorage.getItem('kx_reloaded_for_chunk')) {
    sessionStorage.setItem('kx_reloaded_for_chunk', '1');
    window.location.reload();
  }
});
window.addEventListener('load', () => sessionStorage.removeItem('kx_reloaded_for_chunk'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Map funnel stages (by path) to a once-per-session named event. Names are prefixed
// `app_` so they're distinct from the kanarix.es landing events in the shared
// Umami / GA4 / Clarity projects.
const FUNNEL_EVENTS: Array<{ match: (p: string) => boolean; event: string }> = [
  { match: (p) => p.startsWith('/portal/register'), event: 'app_register_view' },
  { match: (p) => p.startsWith('/portal/verify'), event: 'app_verify_view' },
  { match: (p) => p.startsWith('/portal/agreement'), event: 'app_agreement_view' },
  { match: (p) => p.startsWith('/portal/payment'), event: 'app_payment_view' },
  { match: (p) => p.startsWith('/portal/onboarding'), event: 'app_onboarding_view' },
  { match: (p) => p.startsWith('/portal/dashboard'), event: 'app_portal_active' },
  { match: (p) => p.startsWith('/portal/login'), event: 'app_login_view' },
];

function RouteTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    trackPageview(pathname);
    const hit = FUNNEL_EVENTS.find((f) => f.match(pathname));
    if (hit) trackOnce(hit.event, { path: pathname });
  }, [pathname]);
  return null;
}

// No cookie banner in the app — consent is covered by the Terms & Conditions
// accepted at signup. Default to accepted so analytics + Crisp load.
if (getConsent() === null) setConsent('accepted');
initAnalytics();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <RouteTracker />
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <ConfirmProvider>
            <App />
          </ConfirmProvider>
        </ToastProvider>
      </QueryClientProvider>
    </BrowserRouter>
  </StrictMode>,
);
